import { useState } from "react";
import { Calculator, Plus, Trash2, X } from "lucide-react";
import { evalMoneyExpr, fmtIsk, moneyToIsk } from "@/lib/price";

const inputCls =
  "border border-amber-900/40 bg-[#0d0d12] px-3 py-2.5 text-sm text-amber-100 outline-none transition-colors focus:border-amber-400";

// Itemised cost breakdown: a quiet calculator button next to the cost box — press it, list the
// lines (guide 80.000, fuel 20.000 …), and the sum writes itself into the cost box. The lines
// persist on the row so the breakdown is always one tap away.
export const CostLinesControl = ({ row, onApply, testid }) => {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState([]);

  const openEditor = () => {
    setLines(row.cost_lines?.length ? row.cost_lines.map((l) => ({ ...l })) : [{ label: "", amount: "" }]);
    setOpen(true);
  };
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, li) => (li === i ? { ...l, [k]: v } : l)));
  const lineTotal = (l) => moneyToIsk(evalMoneyExpr(l.amount) ?? l.amount) || 0;
  const total = lines.reduce((s, l) => s + lineTotal(l), 0);

  const apply = () => {
    const kept = lines.filter((l) => (l.label || "").trim() || (l.amount || "").trim());
    onApply(kept, total ? Math.round(total).toLocaleString("de-DE") : "");
    setOpen(false);
  };

  const count = (row.cost_lines || []).length;
  return (
    <>
      <button
        type="button"
        data-testid={testid}
        title={count ? `Cost breakdown — ${count} line${count === 1 ? "" : "s"} (tap to edit)` : "Itemise this cost — the lines sum into the cost box"}
        onClick={openEditor}
        className="relative flex items-center gap-1 border border-neutral-700 px-2 py-2 text-neutral-500 transition-colors hover:border-amber-400 hover:text-amber-200"
      >
        <Calculator className="h-3.5 w-3.5" />
        {count > 0 && (
          <span data-testid={`${testid}-count`} className="text-[9px] font-semibold uppercase tracking-wider text-amber-300/90">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid={`${testid}-modal`} className="fixed inset-0 z-[96] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md border border-amber-500/25 bg-[#0c0c10] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Cost breakdown</p>
                <p className="mt-1 text-[11px] text-neutral-500">List what makes up this cost — the lines sum into the cost box.</p>
              </div>
              <button data-testid={`${testid}-close`} onClick={() => setOpen(false)} className="text-neutral-600 transition-colors hover:text-neutral-300" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {["Driver", "Car", "Gas", "Guide", "Tickets"].map((c) => (
                  <button
                    key={c}
                    data-testid={`${testid}-quick-${c.toLowerCase()}`}
                    onClick={() => setLines((ls) => [...ls.filter((l) => (l.label || "").trim() || (l.amount || "").trim()), { label: c, amount: "" }])}
                    className="border border-neutral-700 px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    + {c}
                  </button>
                ))}
              </div>
              {lines.map((l, li) => (
                <div key={li} data-testid={`${testid}-line-${li}`} className="flex items-center gap-2">
                  <input
                    data-testid={`${testid}-label-${li}`}
                    placeholder="What — e.g. guide, fuel, tickets"
                    value={l.label}
                    onChange={(e) => setLine(li, "label", e.target.value)}
                    className={`${inputCls} min-w-0 flex-1`}
                  />
                  <input
                    data-testid={`${testid}-amount-${li}`}
                    placeholder="80.000"
                    value={l.amount}
                    onChange={(e) => setLine(li, "amount", e.target.value)}
                    className={`${inputCls} !w-32`}
                  />
                  <button data-testid={`${testid}-remove-${li}`} onClick={() => setLines((ls) => ls.filter((_, x) => x !== li))} className="text-neutral-600 transition-colors hover:text-red-400" aria-label="Remove line">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                data-testid={`${testid}-add`}
                onClick={() => setLines((ls) => [...ls, { label: "", amount: "" }])}
                className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
              >
                <Plus className="h-3 w-3" /> Add a line
              </button>
            </div>
            <div className="mt-5 flex items-center justify-between border-t border-neutral-800 pt-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                Total <span data-testid={`${testid}-total`} className="ml-1 font-luxe text-base tracking-wide text-amber-100">{fmtIsk(total)}</span>
              </p>
              <button
                data-testid={`${testid}-apply`}
                onClick={apply}
                className="border border-amber-500/50 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
              >
                Use this total
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
